/**
 * m3 echo Frontend - Queue Tab
 *
 * Job queue monitoring (moved from Settings).
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { settingsApi } from '../../services/api';

export const QueueTab: React.FC = () => {
  const [queueData, setQueueData] = useState<{
    llm_jobs: any[];
    benchmark_tests: any[];
    total_queued: number;
  }>({ llm_jobs: [], benchmark_tests: [], total_queued: 0 });
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);

  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = async () => {
    setQueueLoading(true);
    setQueueError(null);
    try {
      const response = await settingsApi.getQueuedJobs();
      setQueueData(response.data);
    } catch (error: any) {
      console.error('Fehler beim Laden der Queue:', error);
      setQueueError(error.response?.data?.detail || 'Fehler beim Laden der Queue');
    } finally {
      setQueueLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Job-Warteschlange</Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadQueue}
          disabled={queueLoading}
        >
          Aktualisieren
        </Button>
      </Box>

      {queueError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {queueError}
        </Alert>
      )}

      {queueLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {queueData.total_queued > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {queueData.total_queued} Job{queueData.total_queued !== 1 ? 's' : ''} in der Warteschlange
            </Typography>
          )}

          {/* LLM Score Jobs */}
          {queueData.llm_jobs.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                LLM Score Jobs ({queueData.llm_jobs.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Projekt</TableCell>
                      <TableCell>Suchbegriff</TableCell>
                      <TableCell>Modelle</TableCell>
                      <TableCell>Prompts</TableCell>
                      <TableCell>Erstellt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queueData.llm_jobs.map((job: any) => (
                      <TableRow key={job.id}>
                        <TableCell>{job.id}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              job.status === 'pending' ? 'Wartend' :
                              job.is_executing ? 'Wird ausgeführt' :
                              'In Warteschlange'
                            }
                            size="small"
                            color={
                              job.status === 'pending' ? 'default' :
                              job.is_executing ? 'success' :
                              'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>{job.project_name || '-'}</TableCell>
                        <TableCell>{job.search_term || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{job.models}</Typography>
                        </TableCell>
                        <TableCell>{job.prompt_count}</TableCell>
                        <TableCell>
                          {new Date(job.created_at).toLocaleString('de-DE')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Benchmark Tests */}
          {queueData.benchmark_tests.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Optimierungs-Tests ({queueData.benchmark_tests.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Projekt</TableCell>
                      <TableCell>Homepage</TableCell>
                      <TableCell>Fortschritt</TableCell>
                      <TableCell>Erstellt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queueData.benchmark_tests.map((test: any) => (
                      <TableRow key={test.id}>
                        <TableCell>{test.id}</TableCell>
                        <TableCell>
                          <Chip
                            label={test.status === 'running' ? 'Läuft' : 'Wartend'}
                            size="small"
                            color={test.status === 'running' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{test.project_name || '-'}</TableCell>
                        <TableCell>{test.homepage_url || '-'}</TableCell>
                        <TableCell>
                          {test.progress_total
                            ? `${test.progress_current ?? 0} / ${test.progress_total}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {new Date(test.created_at).toLocaleString('de-DE')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {queueData.total_queued === 0 && (
            <Alert severity="info">
              Keine Jobs in der Warteschlange
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QueueTab;
